<template>
    <div class="ticket notranslate">
        <div class="ticket-title">
            <div class="ticket-title-group">
                <span v-if="betSlip.isLive" class="ticket-livetag">
                    {{ getTranslationBySite('live') }}
                </span>
                <SvgIcon v-bind:iconName="iconName" />
                <span class="ticket-league" v-bind:class="{ _live: betSlip.isLive }">
                    {{ leagueName }}
                </span>
            </div>
            <div
                class="ticket-close JARVIS_BeTticket_Btn_DeleteTicket"
                v-on:click="removeTicket"
            >
                <SvgIcon iconName="systemIcon_close" />
            </div>
        </div>
        <div class="ticket-odd">
            <div class="ticket-odd-content">
                <div class="ticket-odd-info">
                    <div class="ticket-marketType">
                        {{ optionName }}
                    </div>
                    <div class="ticket-oddType">
                        {{ option }}
                    </div>
                </div>
                <div class="ticket-odd-main">
                    <div class="ticket-oddValue" v-bind:class="{ _negative: isOddsNegative }">
                        {{ displayPrice }}
                    </div>
                </div>
                <div
                    class="ticket-refresh-btn"
                    v-bind:class="{ _refreshing: isRefreshing }"
                    v-on:click="refresh"
                >
                    <SvgIcon iconName="systemIcon_refresh_centered" style="fill: #000000; font-size: 1.8rem;" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, ref } from 'vue';
    import { SportType } from '@/interface/enum';
    import { hasLastStake } from '@/libraries/betSlip/lastStakeStorage';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { switchToEuroPrice } from '@/libraries/helpers/oddsHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { useOutrightInfoSubscriptionCallback } from '@/libraries/oddsApi/composables/useOutrightInfoSubscriptionCallback';
    import { invertCapitalize } from '@/libraries/string';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useOutrightBetSlipStore } from '@/stores/betSlip/outrightBetSlipStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    const { customerInfo } = storeToRefs(useAccountStore());
    const { betSlip, hasTicket } = storeToRefs(useOutrightBetSlipStore());
    const { removeTicket, refresh: _refresh, updateTicket } = useOutrightBetSlipStore();

    const optionName = computed(() => i18n.t(betSlip.value.marketDisplayTypeName));
    const leagueName = computed(() => getTranslated(betSlip.value.leagueName));
    const option = computed(() => getTranslated(betSlip.value.teamName));

    const displayPrice = computed(() => {
        let price = betSlip.value.price;
        if (betSlip.value.price < 1) {
            price = switchToEuroPrice(
                betSlip.value.price,
                customerInfo.value.priceStyle,
            );
        }
        return formatMinDecimalPlace(price, 2);
    });

    const iconName = computed(() => `sportIcon_${invertCapitalize(SportType[betSlip.value.sportType])}`);
    const isOddsNegative = computed(() => parseFloat(displayPrice.value) < 0);

    function hasShowStakePad() {
        const hasDefaultStake = hasLastStake();
        if (!hasDefaultStake) {
            useBetSlipStore().setIsShowStakePad(false);
        }
    }

    const isRefreshing = ref(false);
    async function refresh() {
        isRefreshing.value = true;
        await _refresh(false, true);
        setTimeout(() => {
            isRefreshing.value = false;
        }, 1000);
    }

    function handleUpdate() {
        refresh();
    }

    function handleDelete(oddsId: number) {
        if (betSlip.value.id !== oddsId) return;
        updateTicket({
            key: betSlip.value.id,
            messageKey: 'betSlip_errorMessage_oddsClosed',
        });
        setTimeout(() => {
            removeTicket();
        }, 3000);
    }

    const {
        onUpdated,
        onDeleted,
    } = useOutrightInfoSubscriptionCallback({
        sportType: betSlip.value.sportType,
        leagueId: betSlip.value.leagueId,
        oddsId: betSlip.value.id,
        isLive: betSlip.value.isLive,
        isActive: hasTicket,
    });
    onUpdated(handleUpdate);
    onDeleted(handleDelete);

    onMounted(() => {
        hasShowStakePad();
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/ticket";
</style>
